import { useState } from 'react';
import { useT } from '../i18n';
import './DataTable.css';

export const DataTable = ({
  columns,
  data,
  onSort,
  onRowClick,
  showCheckbox = false,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  showSettings = true,
  onDelete
}) => {
  const { t } = useT();
  const [sortColumn, setSortColumn] = useState(null);
  const [openMenuRow, setOpenMenuRow] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (columnId) => {
    const newDirection = sortColumn === columnId && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(columnId);
    setSortDirection(newDirection);
    if (onSort) {
      onSort(columnId, newDirection);
    }
  };

  const allSelected = data.length > 0 && selectedRows.length === data.length;

  return (
    <div className="data-table-container">
      <table className="data-table">
        <thead>
          <tr>
            {showCheckbox && (
              <th className="checkbox-column">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onSelectAll && onSelectAll(e.target.checked)}
                />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.id}
                className={column.sortable ? 'sortable' : ''}
                onClick={() => column.sortable && handleSort(column.id)}
              >
                <div className="th-content">
                  {column.label}
                  {column.sortable && (
                    <span className="sort-icon">
                      {sortColumn === column.id ? (sortDirection === 'asc' ? '↑' : '↓') : '⇅'}
                    </span>
                  )}
                </div>
              </th>
            ))}
            {showSettings && (
              <th className="settings-column">
                <button className="settings-button">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.5 1.5L6.79 3.21C7.12 3.32 7.43 3.47 7.71 3.66L9.35 3.05L10.35 4.78L9.03 5.95C9.07 6.3 9.07 6.64 9.03 6.99L10.35 8.15L9.35 9.88L7.72 9.27C7.44 9.46 7.12 9.61 6.79 9.72L6.5 11.42H4.5L4.21 9.71C3.88 9.6 3.57 9.45 3.29 9.26L1.65 9.87L0.65 8.14L1.97 6.98C1.93 6.63 1.93 6.29 1.97 5.94L0.65 4.78L1.65 3.05L3.28 3.66C3.56 3.47 3.88 3.32 4.21 3.21L4.5 1.5H6.5ZM5.5 4.5C4.12 4.5 3 5.62 3 7C3 8.38 4.12 9.5 5.5 9.5C6.88 9.5 8 8.38 8 7C8 5.62 6.88 4.5 5.5 4.5Z" fill="#9CA3AF"/>
                  </svg>
                </button>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (showCheckbox ? 1 : 0) + (showSettings ? 1 : 0)} className="no-data">
                {t('dtbl.noData')}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr
                key={row.id || index}
                className={`${onRowClick ? 'clickable' : ''} ${selectedRows.includes(row.id) ? 'selected' : ''}`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {showCheckbox && (
                  <td className="checkbox-column" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row.id)}
                      onChange={(e) => onSelectRow && onSelectRow(row.id, e.target.checked)}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.id}>
                    {column.render ? column.render(row[column.field], row) : row[column.field]}
                  </td>
                ))}
                {showSettings && (
                  <td className="actions-column" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
                    <button
                      className="action-menu-button"
                      onClick={() => onDelete && setOpenMenuRow(openMenuRow === (row.id ?? index) ? null : (row.id ?? index))}
                    >
                      <span>•••</span>
                    </button>
                    {onDelete && openMenuRow === (row.id ?? index) && (
                      <>
                        <div
                          onClick={() => setOpenMenuRow(null)}
                          style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                        />
                        <div style={{
                          position: 'absolute', right: '8px', top: '100%', zIndex: 50,
                          background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '150px', overflow: 'hidden'
                        }}>
                          <button
                            onClick={() => { setOpenMenuRow(null); onDelete(row); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                              padding: '10px 14px', border: 'none', background: 'transparent',
                              color: '#dc2626', fontSize: '13px', cursor: 'pointer',
                              textAlign: 'left', whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#fef2f2')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                          >
                            🗑️ {t('dtbl.delete')}
                          </button>
                        </div>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
